import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { PermissionAccess } from '@trucktrax/trucktrax-ts-common';
import { withRouter } from 'react-router-dom';
import DataTableContent from '../../shared/DataTableContent';
import { addDataTest, addHeaderDataTest } from '../../../util/adminUtil';
import RegionsAddModal from './RegionsAddModal';
import { REGIONS_TEXT } from '../../../constants/navConstants';
import {
  ADMIN_KEYS,
  ADMIN_LABELS,
  AGGREGATE_VALUE,
  CEMENT_VALUE,
  NOT_AVAILABLE,
  READYMIX_VALUE,
} from '../../../constants/appConstants';
import {
  ADMIN_REGIONS_DEACTIVATED_PATH,
  ADMIN_REGIONS_PATH,
  DATASOURCE,
  REGIONS_PATH,
} from '../../../constants/apiConstants';
import { getGeotraxBaseUrl } from '../../../util/apiUtil';
import { ReduxState } from '../../../store';
import { isUserWithAtleastOneOfGivenPermissions } from '../../../util/permissionUtil';
import { UserPermissionsByRegion, AssignedRegionDto } from '../../../types';
import { isEmptyObject, isNotEmptyString } from '../../../util/appUtil';
import { devErrorAndLog } from '../../../util/errorUtil';
import styles from './RegionsView.module.css';

const DEACTIVATED_TAB = 1;

export class RegionsView extends Component<RegionsViewProps, RegionsViewState> {
  state = {
    isOpen: false,
    isCloseRequest: false,
  };

  static abbreviateProductLines = (sourceData: string) => {
    let data = sourceData;
    if (!data || data.length === 0) {
      return NOT_AVAILABLE;
    }

    try {
      if (Array.isArray(data)) {
        data = data.join();
      }
      const abbreviatedProductLines: string = data
        .split(',')
        .map(item => {
          switch (item) {
            case AGGREGATE_VALUE:
              return 'AGG';
            case CEMENT_VALUE:
              return 'CEM';
            case READYMIX_VALUE:
              return 'RM';
            default:
              return NOT_AVAILABLE;
          }
        })
        ?.join(', ');
      return abbreviatedProductLines;
    } catch (err: any) {
      devErrorAndLog(
        'Encountered an error abbreviating and combining product lines',
        `Regions ${err.toString()}`,
        undefined,
        undefined,
        true,
      );
      return NOT_AVAILABLE;
    }
  };

  static getColumns = () => [
    {
      Header: addHeaderDataTest(null, 'sort-region-by-name', ADMIN_LABELS.NAME),
      accessor: ADMIN_KEYS.NAME,
      minWidth: 150,
      Cell: (props: any) => addDataTest(props, 'admin-region-name', props.value),
    },
    {
      Header: addHeaderDataTest(null, 'sort-region-by-product-lines', ADMIN_LABELS.PRODUCT_LINES),
      accessor: ADMIN_KEYS.PRODUCT_LINES,
      minWidth: 100,
      Cell: (props: { value: string }) =>
        addDataTest(
          null,
          'admin-region-product-lines',
          RegionsView.abbreviateProductLines(props.value),
        ),
    },
    {
      Header: addHeaderDataTest(null, 'sort-region-by-description', ADMIN_LABELS.DESCRIPTION),
      accessor: ADMIN_KEYS.DESCRIPTION,
      minWidth: 200,
      sortable: false,
      Cell: (props: any) => addDataTest(props, 'admin-region-description', props.value),
    },
    {
      Header: addHeaderDataTest(null, 'sort-region-by-latitude', ADMIN_LABELS.LATITUDE),
      accessor: ADMIN_KEYS.LATITUDE,
      minWidth: 200,
      sortable: false,
      Cell: (props: any) => addDataTest(props, 'admin-region-latitude', props.value),
    },
    {
      Header: addHeaderDataTest(null, 'sort-region-by-longitude', ADMIN_LABELS.LONGITUDE),
      accessor: ADMIN_KEYS.LONGITUDE,
      minWidth: 200,
      sortable: false,
      Cell: (props: any) => addDataTest(props, 'admin-region-longitude', props.value),
    },
  ];

  requestCloseModal = () => {
    this.setState({
      isOpen: true,
      isCloseRequest: true,
    });
  };

  closeModal = () => {
    this.setState({
      isOpen: false,
      isCloseRequest: false,
    });
  };

  openModal = () => {
    this.setState({
      isOpen: true,
      isCloseRequest: false,
    });
  };

  handleTabChange = (_event: unknown, newValue: number) => {
    this.props.history.push(
      newValue === DEACTIVATED_TAB ? ADMIN_REGIONS_DEACTIVATED_PATH : ADMIN_REGIONS_PATH,
    );
  };

  hasRegionPermission = (url?: string) =>
    isUserWithAtleastOneOfGivenPermissions(this.props.userPermission, url ?? '', [REGIONS_TEXT]);

  render() {
    // wait until permissions object is passed
    if (isEmptyObject(this.props.userPermission)) {
      return <></>;
    }
    const isDeactivatedTab = this.props.tab === DEACTIVATED_TAB;
    // Active: the user's assigned regions they hold VIEW or EDIT in. Deactivated regions are no
    // longer assigned, so that tab asks for every region the user holds permission in instead.
    const regionList = (
      isDeactivatedTab
        ? Object.keys(this.props.userPermission)
        : this.props.regions.map(region => region.url)
    )
      .filter(url => isNotEmptyString(url) && this.hasRegionPermission(url))
      .join(',');

    const regionsUrl = getGeotraxBaseUrl() + REGIONS_PATH;
    const baseUrl = `${regionsUrl}/paged-assignment?destination=${regionList}`;
    const columns = RegionsView.getColumns();
    const hasPermissionAccess = this.props.regionsPermissionAccess === PermissionAccess.Edit;

    const tabs = (
      <Tabs
        value={this.props.tab ?? 0}
        onChange={this.handleTabChange}
        classes={{ root: styles.tabsContainer }}
      >
        <Tab label="Active" data-test="regions-active-tab" />
        <Tab label="Deactivated" data-test="regions-deactivated-tab" />
      </Tabs>
    );

    return (
      <div className={styles.container}>
        <DataTableContent
          baseUrl={baseUrl}
          columns={columns}
          header={REGIONS_TEXT}
          noRegionFilter
          renderTabs={tabs}
          isArchived={isDeactivatedTab}
          canAddNewItem={hasPermissionAccess && !isDeactivatedTab}
          source={DATASOURCE.REGION}
        />
        {hasPermissionAccess && (
          <RegionsAddModal
            isOpen={this.state.isOpen}
            openModal={this.openModal}
            closeModal={this.requestCloseModal}
            discardAcceptAction={this.closeModal}
            discardRejectAction={this.openModal}
            isCloseRequest={this.state.isCloseRequest}
            history={this.props.history}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  regionsPermissionAccess: state.adminPermissionAccess[REGIONS_TEXT] ?? '',
  regions: state.assignedRegionList || {},
  userPermission: state.userPermission || {},
});

export interface RegionsViewProps {
  regionsPermissionAccess: string;
  regions: AssignedRegionDto[];
  userPermission: UserPermissionsByRegion;
  tab?: number;
  history: {
    push: (path: string) => void;
    location: {
      pathname: string;
    };
  };
}

export interface RegionsViewState {
  isOpen: boolean;
  isCloseRequest: boolean;
}

export default withRouter(connect(mapStateToProps)(RegionsView) as any);
